import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Plus, Upload, Trash2, Edit2, RefreshCw, Send, Download, Users, Sparkles, Image, MessageSquare, Clock, StopCircle, CheckCircle2, XCircle, Timer, Search, X, TestTube, Radio, ChevronDown, AlertTriangle, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

interface Consulente {
  id: string;
  nome: string;
  sobrenome: string | null;
  telefone: string;
  ativo: boolean;
  created_at: string;
}

interface SendProgress {
  atual: number;
  total: number;
  enviados: number;
  erros: number;
  consulenteAtual: string;
}

interface PhoneValidationResult {
  valid: boolean;
  reason?: string;
}

// Common country codes for validation
const VALID_COUNTRY_CODES = ['1', '55', '351', '44', '34', '33', '49', '39', '54', '56', '57', '58', '52', '595', '598'];

// Phone number validation function
const isValidPhoneNumber = (phone: string): PhoneValidationResult => {
  if (!phone || phone.trim() === '') {
    return { valid: false, reason: 'Telefone vazio' };
  }

  // Remove spaces, dashes, parentheses
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
  
  // Check for invalid characters (only numbers and optional + at start)
  if (!/^\+?[0-9]+$/.test(cleaned)) {
    return { valid: false, reason: 'Caracteres inválidos' };
  }
  
  // Remove + for digit counting
  const digits = cleaned.replace('+', '');
  
  // Check minimum length (E.164: 10-15 digits)
  if (digits.length < 10) {
    return { valid: false, reason: `Muito curto (${digits.length} dígitos)` };
  }
  
  if (digits.length > 15) {
    return { valid: false, reason: `Muito longo (${digits.length} dígitos)` };
  }
  
  // Check for valid country code
  const hasValidCountryCode = VALID_COUNTRY_CODES.some(code => digits.startsWith(code));
  
  if (!hasValidCountryCode) {
    return { valid: false, reason: 'Código de país não reconhecido' };
  }
  
  return { valid: true };
};

export const WhatsAppCampaignTab = () => {
  const { toast } = useToast();
  
  // Consulentes state
  const [consulentes, setConsulentes] = useState<Consulente[]>([]);
  const [loadingConsulentes, setLoadingConsulentes] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedConsulente, setSelectedConsulente] = useState<Consulente | null>(null);
  const [newConsulente, setNewConsulente] = useState({ nome: '', sobrenome: '', telefone: '' });
  const [csvData, setCsvData] = useState<{ nome: string; sobrenome: string; telefone: string }[]>([]);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Tab state for valid/invalid
  const [validationTab, setValidationTab] = useState<'valid' | 'invalid'>('valid');

  // Campaign state
  const [tema, setTema] = useState('');
  const [descricaoImagem, setDescricaoImagem] = useState('');
  const [contextoCaption, setContextoCaption] = useState('');
  const [linkSite, setLinkSite] = useState('https://tupja.com');
  const [textoFaixa, setTextoFaixa] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [generatedCaption, setGeneratedCaption] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // Sending state with progress
  const [sending, setSending] = useState(false);
  const [intervalo, setIntervalo] = useState(5);
  const [progress, setProgress] = useState<SendProgress>({
    atual: 0,
    total: 0,
    enviados: 0,
    erros: 0,
    consulenteAtual: ''
  });
  const cancelRef = useRef(false);

  // Settings state
  const [testMode, setTestMode] = useState(true);
  const [n8nEndpoint, setN8nEndpoint] = useState('');
  const [loadingSettings, setLoadingSettings] = useState(true);

  // UI state
  const [consultentesExpanded, setConsultentesExpanded] = useState(false);

  // Computed validation results
  const validationResults = useMemo(() => {
    const results = new Map<string, PhoneValidationResult>();
    consulentes.forEach(c => {
      results.set(c.id, isValidPhoneNumber(c.telefone));
    });
    return results;
  }, [consulentes]);

  // Filter consulentes by validity and search term
  const { validConsulentes, invalidConsulentes } = useMemo(() => {
    const valid: Consulente[] = [];
    const invalid: Consulente[] = [];
    
    consulentes.forEach(c => {
      const result = validationResults.get(c.id);
      if (result?.valid) {
        valid.push(c);
      } else {
        invalid.push(c);
      }
    });
    
    return { validConsulentes: valid, invalidConsulentes: invalid };
  }, [consulentes, validationResults]);

  // Filter by search term
  const filteredConsulentes = useMemo(() => {
    const baseList = validationTab === 'valid' ? validConsulentes : invalidConsulentes;
    
    if (!searchTerm) return baseList;
    
    const search = searchTerm.toLowerCase();
    return baseList.filter(c => 
      c.nome.toLowerCase().includes(search) ||
      (c.sobrenome?.toLowerCase() || '').includes(search) ||
      c.telefone.includes(search)
    );
  }, [validConsulentes, invalidConsulentes, validationTab, searchTerm]);

  // Count of valid AND active consulentes (for campaign sending)
  const validAndActiveCount = useMemo(() => {
    return validConsulentes.filter(c => c.ativo).length;
  }, [validConsulentes]);

  const activeCount = consulentes.filter(c => c.ativo).length;

  useEffect(() => {
    fetchConsulentes();
    fetchSettings();
  }, []);

  // Auto-deactivate invalid numbers when consulentes are loaded
  useEffect(() => {
    if (!loadingConsulentes && invalidConsulentes.length > 0) {
      const activeInvalids = invalidConsulentes.filter(c => c.ativo);
      if (activeInvalids.length > 0) {
        deactivateInvalidNumbers(activeInvalids);
      }
    }
  }, [loadingConsulentes, invalidConsulentes]);

  const deactivateInvalidNumbers = async (activeInvalids: Consulente[]) => {
    try {
      const ids = activeInvalids.map(c => c.id);
      
      const { error } = await supabase
        .from('consulentes')
        .update({ ativo: false })
        .in('id', ids);

      if (error) throw error;

      toast({
        title: "Telefones inválidos desativados",
        description: `${activeInvalids.length} consulente(s) com telefone inválido foram desativados automaticamente`,
        variant: "destructive",
      });

      // Refresh the list
      fetchConsulentes();
    } catch (error: any) {
      console.error('Error deactivating invalid numbers:', error);
    }
  };

  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('key, value');

      if (error) throw error;

      data?.forEach(setting => {
        if (setting.key === 'n8n_webhook_url') {
          setN8nEndpoint(setting.value);
        } else if (setting.key === 'whatsapp_test_mode') {
          setTestMode(setting.value === 'true');
        }
      });
    } catch (error: any) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchConsulentes = async () => {
    setLoadingConsulentes(true);
    try {
      const { data, error } = await supabase
        .from('consulentes')
        .select('*')
        .order('nome', { ascending: true });

      if (error) throw error;
      setConsulentes(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar consulentes",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingConsulentes(false);
    }
  };

  const handleAddConsulente = async () => {
    if (!newConsulente.nome || !newConsulente.telefone) {
      toast({
        title: "Campos obrigatórios",
        description: "Nome e telefone são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    // Validate phone before adding
    const validation = isValidPhoneNumber(newConsulente.telefone);
    if (!validation.valid) {
      toast({
        title: "Telefone inválido",
        description: validation.reason || "O telefone informado não é válido",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('consulentes')
        .insert({
          nome: newConsulente.nome,
          sobrenome: newConsulente.sobrenome || null,
          telefone: newConsulente.telefone,
        });

      if (error) throw error;

      toast({
        title: "Consulente adicionado",
        description: `${newConsulente.nome} foi adicionado com sucesso`,
      });

      setNewConsulente({ nome: '', sobrenome: '', telefone: '' });
      setAddDialogOpen(false);
      fetchConsulentes();
    } catch (error: any) {
      toast({
        title: "Erro ao adicionar",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateConsulente = async () => {
    if (!selectedConsulente) return;

    // Validate phone before updating
    const validation = isValidPhoneNumber(selectedConsulente.telefone);
    
    try {
      const { error } = await supabase
        .from('consulentes')
        .update({
          nome: selectedConsulente.nome,
          sobrenome: selectedConsulente.sobrenome,
          telefone: selectedConsulente.telefone,
          // If phone is now valid, keep current active state; if invalid, force deactivate
          ativo: validation.valid ? selectedConsulente.ativo : false,
        })
        .eq('id', selectedConsulente.id);

      if (error) throw error;

      const message = validation.valid 
        ? `${selectedConsulente.nome} foi atualizado`
        : `${selectedConsulente.nome} foi atualizado mas desativado (telefone inválido)`;

      toast({
        title: "Consulente atualizado",
        description: message,
        variant: validation.valid ? "default" : "destructive",
      });

      setEditDialogOpen(false);
      setSelectedConsulente(null);
      fetchConsulentes();
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (consulente: Consulente) => {
    // Don't allow activating invalid numbers
    const validation = validationResults.get(consulente.id);
    if (!consulente.ativo && !validation?.valid) {
      toast({
        title: "Não é possível ativar",
        description: `Telefone inválido: ${validation?.reason}. Corrija o número antes de ativar.`,
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('consulentes')
        .update({ ativo: !consulente.ativo })
        .eq('id', consulente.id);

      if (error) throw error;

      toast({
        title: consulente.ativo ? "Consulente desativado" : "Consulente ativado",
        description: `${consulente.nome} foi ${consulente.ativo ? 'desativado' : 'ativado'}`,
      });

      fetchConsulentes();
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar status",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteConsulente = async (consulente: Consulente) => {
    try {
      const { error } = await supabase
        .from('consulentes')
        .delete()
        .eq('id', consulente.id);

      if (error) throw error;

      toast({
        title: "Consulente removido",
        description: `${consulente.nome} foi removido`,
      });

      fetchConsulentes();
    } catch (error: any) {
      toast({
        title: "Erro ao remover",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header
      const dataLines = lines.slice(1);
      const parsed = dataLines.map(line => {
        const [nome, sobrenome, telefone] = line.split(',').map(s => s.trim());
        return { nome: nome || '', sobrenome: sobrenome || '', telefone: telefone || '' };
      }).filter(item => item.nome && item.telefone);

      setCsvData(parsed);
      setImportDialogOpen(true);
    };
    reader.readAsText(file);
  };

  const handleImportCSV = async () => {
    if (csvData.length === 0) return;

    setImporting(true);
    let imported = 0;
    let skipped = 0;

    try {
      for (const item of csvData) {
        const { error } = await supabase
          .from('consulentes')
          .insert({
            nome: item.nome,
            sobrenome: item.sobrenome || null,
            telefone: item.telefone,
          });

        if (error) {
          if (error.code === '23505') { // Unique violation
            skipped++;
          } else {
            console.error('Import error:', error);
          }
        } else {
          imported++;
        }
      }

      toast({
        title: "Importação concluída",
        description: `${imported} importados, ${skipped} duplicados ignorados`,
      });

      setImportDialogOpen(false);
      setCsvData([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchConsulentes();
    } catch (error: any) {
      toast({
        title: "Erro na importação",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleGenerateCampaign = async () => {
    if (!tema) {
      toast({
        title: "Tema obrigatório",
        description: "Por favor, informe o tema da campanha",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    setGeneratedImageUrl('');
    setGeneratedCaption('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Não autenticado');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-whatsapp-campaign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          tema, 
          descricaoImagem, 
          contextoCaption, 
          linkSite, 
          textoFaixa 
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Falha ao gerar campanha');
      }

      setGeneratedImageUrl(data.imageUrl);
      setGeneratedCaption(data.caption);

      toast({
        title: "Campanha gerada!",
        description: "Imagem e texto criados com sucesso",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao gerar campanha",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateImage = async () => {
    await handleGenerateCampaign();
  };

  const handleSendCampaign = async () => {
    if (!generatedImageUrl || !generatedCaption) {
      toast({
        title: "Campanha incompleta",
        description: "Gere a campanha antes de enviar",
        variant: "destructive",
      });
      return;
    }

    // IMPORTANT: Only send to VALID AND ACTIVE consulentes
    const validAndActiveConsulentes = validConsulentes.filter(c => c.ativo);
    
    if (validAndActiveConsulentes.length === 0) {
      toast({
        title: "Nenhum destinatário",
        description: "Não há consulentes válidos e ativos para enviar",
        variant: "destructive",
      });
      return;
    }

    // Apply test mode - only send to first consulente
    const consultesToSend = testMode ? validAndActiveConsulentes.slice(0, 1) : validAndActiveConsulentes;

    // Reset cancel flag and progress
    cancelRef.current = false;
    setSending(true);
    setProgress({
      atual: 0,
      total: consultesToSend.length,
      enviados: 0,
      erros: 0,
      consulenteAtual: ''
    });

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Erro",
        description: "Não autenticado",
        variant: "destructive",
      });
      setSending(false);
      return;
    }

    let enviados = 0;
    let erros = 0;

    for (let i = 0; i < consultesToSend.length; i++) {
      // Check if cancelled
      if (cancelRef.current) {
        toast({
          title: "Envio cancelado",
          description: `${enviados} enviados, ${erros} erros, ${consultesToSend.length - i} restantes`,
        });
        break;
      }

      const c = consultesToSend[i];
      const nomeCompleto = `${c.nome} ${c.sobrenome || ''}`.trim();

      // Update progress
      setProgress(prev => ({
        ...prev,
        atual: i + 1,
        consulenteAtual: nomeCompleto
      }));

      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-single-whatsapp`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            imageUrl: generatedImageUrl,
            caption: generatedCaption,
            nome: c.nome,
            sobrenome: c.sobrenome || '',
            telefone: c.telefone,
            webhookUrl: n8nEndpoint, // Pass the configured endpoint
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          enviados++;
          setProgress(prev => ({ ...prev, enviados }));
        } else {
          erros++;
          setProgress(prev => ({ ...prev, erros }));
          console.error(`Error sending to ${nomeCompleto}:`, data.error);
        }
      } catch (error: any) {
        erros++;
        setProgress(prev => ({ ...prev, erros }));
        console.error(`Error sending to ${nomeCompleto}:`, error.message);
      }

      // Wait interval before next send (except for last one)
      if (i < consultesToSend.length - 1 && !cancelRef.current) {
        await new Promise(resolve => setTimeout(resolve, intervalo * 1000));
      }
    }

    if (!cancelRef.current) {
      const modeLabel = testMode ? ' (Modo Teste)' : '';
      toast({
        title: `Campanha finalizada!${modeLabel}`,
        description: `${enviados} enviados com sucesso, ${erros} erros`,
      });

      // Clear campaign after successful send
      if (erros === 0) {
        setTema('');
        setDescricaoImagem('');
        setContextoCaption('');
        setTextoFaixa('');
        setGeneratedImageUrl('');
        setGeneratedCaption('');
      }
    }

    setSending(false);
  };

  const handleCancelSend = () => {
    cancelRef.current = true;
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.atual / progress.total) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Consulentes Management Section */}
      <Card>
        <Collapsible open={consultentesExpanded} onOpenChange={setConsultentesExpanded}>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-xl font-display text-primary">
                  <Users className="w-5 h-5" />
                  Consulentes
                </CardTitle>
                <CardDescription>
                  Gerencie a lista de contatos para campanhas WhatsApp
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="default" className="bg-green-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {validConsulentes.length} válidos
                </Badge>
                <Badge variant="destructive">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {invalidConsulentes.length} inválidos
                </Badge>
                <Badge variant="secondary">
                  <Phone className="w-3 h-3 mr-1" />
                  {validAndActiveCount} para envio
                </Badge>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="hidden"
                />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Adicionar Consulente</DialogTitle>
                      <DialogDescription>
                        Preencha os dados do novo contato. O telefone deve incluir código do país.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="nome">Nome *</Label>
                        <Input
                          id="nome"
                          value={newConsulente.nome}
                          onChange={(e) => setNewConsulente({ ...newConsulente, nome: e.target.value })}
                          placeholder="João"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sobrenome">Sobrenome</Label>
                        <Input
                          id="sobrenome"
                          value={newConsulente.sobrenome}
                          onChange={(e) => setNewConsulente({ ...newConsulente, sobrenome: e.target.value })}
                          placeholder="Silva"
                        />
                      </div>
                      <div>
                        <Label htmlFor="telefone">Telefone * (com código do país)</Label>
                        <Input
                          id="telefone"
                          value={newConsulente.telefone}
                          onChange={(e) => setNewConsulente({ ...newConsulente, telefone: e.target.value })}
                          placeholder="5511999999999"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Ex: 5511999999999 (Brasil), 1555555555 (EUA)
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancelar</Button>
                      <Button onClick={handleAddConsulente}>Adicionar</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="icon" title={consultentesExpanded ? "Minimizar" : "Expandir"}>
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      consultentesExpanded && "rotate-180"
                    )} />
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              {loadingConsulentes ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : consulentes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum consulente cadastrado. Adicione manualmente ou importe um CSV.
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Search field */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Pesquisar por nome, sobrenome ou telefone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-9"
                    />
                    {searchTerm && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
                        onClick={() => setSearchTerm('')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Tabs for Valid/Invalid */}
                  <Tabs value={validationTab} onValueChange={(v) => setValidationTab(v as 'valid' | 'invalid')}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="valid" className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Válidos ({validConsulentes.length})
                      </TabsTrigger>
                      <TabsTrigger value="invalid" className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Inválidos ({invalidConsulentes.length})
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="valid" className="mt-3">
                      {searchTerm && (
                        <p className="text-sm text-muted-foreground mb-2">
                          Mostrando {filteredConsulentes.length} de {validConsulentes.length} consulentes válidos
                        </p>
                      )}
                      <div className="rounded-md border max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead>Sobrenome</TableHead>
                              <TableHead>Telefone</TableHead>
                              <TableHead className="text-center">Ativo</TableHead>
                              <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredConsulentes.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                                  {searchTerm ? `Nenhum consulente válido encontrado para "${searchTerm}"` : 'Nenhum consulente com telefone válido'}
                                </TableCell>
                              </TableRow>
                            ) : filteredConsulentes.map((consulente) => (
                              <TableRow key={consulente.id}>
                                <TableCell className="font-medium">{consulente.nome}</TableCell>
                                <TableCell>{consulente.sobrenome || '-'}</TableCell>
                                <TableCell>
                                  <span className="flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                    {consulente.telefone}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Switch
                                    checked={consulente.ativo}
                                    onCheckedChange={() => handleToggleActive(consulente)}
                                  />
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setSelectedConsulente(consulente);
                                        setEditDialogOpen(true);
                                      }}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="icon">
                                          <Trash2 className="w-4 h-4 text-destructive" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Deseja remover {consulente.nome}? Esta ação não pode ser desfeita.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => handleDeleteConsulente(consulente)}>
                                            Excluir
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>

                    <TabsContent value="invalid" className="mt-3">
                      {invalidConsulentes.length > 0 && (
                        <Alert variant="destructive" className="mb-3">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            Telefones inválidos são automaticamente desativados e não receberão campanhas. Corrija os números para reativá-los.
                          </AlertDescription>
                        </Alert>
                      )}
                      {searchTerm && (
                        <p className="text-sm text-muted-foreground mb-2">
                          Mostrando {filteredConsulentes.length} de {invalidConsulentes.length} consulentes inválidos
                        </p>
                      )}
                      <div className="rounded-md border max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead>Sobrenome</TableHead>
                              <TableHead>Telefone</TableHead>
                              <TableHead>Motivo</TableHead>
                              <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredConsulentes.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                                  {searchTerm ? `Nenhum consulente inválido encontrado para "${searchTerm}"` : '🎉 Todos os telefones estão válidos!'}
                                </TableCell>
                              </TableRow>
                            ) : filteredConsulentes.map((consulente) => {
                              const validation = validationResults.get(consulente.id);
                              return (
                                <TableRow key={consulente.id} className="bg-destructive/5">
                                  <TableCell className="font-medium">{consulente.nome}</TableCell>
                                  <TableCell>{consulente.sobrenome || '-'}</TableCell>
                                  <TableCell>
                                    <span className="flex items-center gap-1 text-destructive">
                                      <XCircle className="w-3 h-3" />
                                      {consulente.telefone || '(vazio)'}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="destructive" className="text-xs">
                                      {validation?.reason || 'Inválido'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setSelectedConsulente(consulente);
                                          setEditDialogOpen(true);
                                        }}
                                        title="Editar para corrigir"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button variant="ghost" size="icon">
                                            <Trash2 className="w-4 h-4 text-destructive" />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Deseja remover {consulente.nome}? Esta ação não pode ser desfeita.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => handleDeleteConsulente(consulente)}>
                                              Excluir
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Consulente</DialogTitle>
            <DialogDescription>
              O telefone deve incluir código do país (ex: 5511999999999)
            </DialogDescription>
          </DialogHeader>
          {selectedConsulente && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-nome">Nome</Label>
                <Input
                  id="edit-nome"
                  value={selectedConsulente.nome}
                  onChange={(e) => setSelectedConsulente({ ...selectedConsulente, nome: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-sobrenome">Sobrenome</Label>
                <Input
                  id="edit-sobrenome"
                  value={selectedConsulente.sobrenome || ''}
                  onChange={(e) => setSelectedConsulente({ ...selectedConsulente, sobrenome: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-telefone">Telefone (com código do país)</Label>
                <Input
                  id="edit-telefone"
                  value={selectedConsulente.telefone}
                  onChange={(e) => setSelectedConsulente({ ...selectedConsulente, telefone: e.target.value })}
                />
                {selectedConsulente.telefone && (() => {
                  const validation = isValidPhoneNumber(selectedConsulente.telefone);
                  return validation.valid ? (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Telefone válido
                    </p>
                  ) : (
                    <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> {validation.reason}
                    </p>
                  );
                })()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateConsulente}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Preview Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview da Importação</DialogTitle>
            <DialogDescription>
              {csvData.length} contatos encontrados no CSV. Telefones inválidos serão importados mas ficarão desativados.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Sobrenome</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {csvData.slice(0, 10).map((item, index) => {
                  const validation = isValidPhoneNumber(item.telefone);
                  return (
                    <TableRow key={index}>
                      <TableCell>{item.nome}</TableCell>
                      <TableCell>{item.sobrenome || '-'}</TableCell>
                      <TableCell>{item.telefone}</TableCell>
                      <TableCell>
                        {validation.valid ? (
                          <Badge variant="default" className="bg-green-600 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Válido
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-xs">
                            <XCircle className="w-3 h-3 mr-1" />
                            {validation.reason}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {csvData.length > 10 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      ... e mais {csvData.length - 10} contatos
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportDialogOpen(false);
              setCsvData([]);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}>
              Cancelar
            </Button>
            <Button onClick={handleImportCSV} disabled={importing}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Importar {csvData.length} contatos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Creation Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl font-display text-primary">
            <Sparkles className="w-5 h-5" />
            Criar Campanha
          </CardTitle>
          <CardDescription>
            Use IA para gerar imagem e texto para sua campanha WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tema da Campanha */}
          <div>
            <Label htmlFor="tema" className="text-base font-medium">Tema da Campanha *</Label>
            <Input
              id="tema"
              value={tema}
              onChange={(e) => setTema(e.target.value)}
              placeholder="Ex: BIBLIOTECA DIGITAL - TUPJA"
              className="mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Este texto aparecerá no topo da imagem e no título da mensagem
            </p>
          </div>

          {/* Link do Site */}
          <div>
            <Label htmlFor="linkSite" className="text-base font-medium">Link do Site (opcional)</Label>
            <Input
              id="linkSite"
              value={linkSite}
              onChange={(e) => setLinkSite(e.target.value)}
              placeholder="https://tupja.com"
              className="mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Será incluído <strong>uma única vez</strong> no final do texto. NÃO aparece na imagem.
            </p>
          </div>

          {/* Descrição da Imagem */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Label htmlFor="descricaoImagem" className="text-base font-medium">Descrição da Imagem</Label>
              <Badge variant="outline" className="text-xs">Descreva a cena</Badge>
            </div>
            <Textarea
              id="descricaoImagem"
              value={descricaoImagem}
              onChange={(e) => setDescricaoImagem(e.target.value)}
              placeholder={`Exemplo: Mulher de branco pegando um livro de Umbanda em uma prateleira de madeira rústica. Ambiente acolhedor com velas acesas ao fundo, luz suave entrando por uma janela com vitral colorido. Tons quentes de marrom, dourado e âmbar.`}
              rows={4}
              className="mt-1.5"
            />
            <div className="mt-2 p-3 bg-muted/50 rounded-lg text-xs space-y-2">
              <p className="font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Como descrever bem a imagem:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Pessoas:</strong> "mulher de branco", "idoso sábio com barba", "grupo em círculo"</li>
                <li><strong>Ambiente:</strong> "prateleira de livros antigos", "altar com velas", "natureza"</li>
                <li><strong>Ação:</strong> "pegando livro", "meditando", "acendendo vela"</li>
                <li><strong>Cores:</strong> "tons quentes", "luz dourada", "ambiente acolhedor"</li>
              </ul>
            </div>
          </div>

          {/* Texto da Faixa na Imagem */}
          <div>
            <Label htmlFor="textoFaixa" className="text-base font-medium">Texto da Faixa na Imagem (opcional)</Label>
            <Input
              id="textoFaixa"
              value={textoFaixa}
              onChange={(e) => setTextoFaixa(e.target.value)}
              placeholder="Ex: Baixe agora em nosso site"
              className="mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Texto que aparecerá em uma faixa na parte inferior da imagem. Deixe vazio para não ter faixa.
            </p>
          </div>

          {/* Contexto para Caption */}
          <div>
            <Label htmlFor="contextoCaption" className="text-base font-medium">Contexto para o Texto (opcional)</Label>
            <Textarea
              id="contextoCaption"
              value={contextoCaption}
              onChange={(e) => setContextoCaption(e.target.value)}
              placeholder="Ex: Livros de Umbanda gratuitos para download, estudos sobre orixás, história da religião. A biblioteca já tem mais de 50 livros disponíveis."
              rows={2}
              className="mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Informações extras que a IA usará para criar o texto da mensagem
            </p>
          </div>

          <Button onClick={handleGenerateCampaign} disabled={generating || !tema} className="w-full" size="lg">
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando com IA...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Gerar Campanha com IA
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Preview Section */}
      {(generatedImageUrl || generatedCaption) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl font-display text-primary">
              <Image className="w-5 h-5" />
              Preview da Campanha
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Image Preview */}
              <div className="space-y-4">
                <Label>Imagem</Label>
                {generatedImageUrl ? (
                  <div className="rounded-lg overflow-hidden border">
                    <img
                      src={generatedImageUrl}
                      alt="Campanha"
                      className="w-full aspect-square object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center">
                    <span className="text-muted-foreground">Nenhuma imagem gerada</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleRegenerateImage} disabled={generating} className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Nova Imagem
                  </Button>
                  {generatedImageUrl && (
                    <Button variant="outline" asChild>
                      <a href={generatedImageUrl} download target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>

              {/* Caption Preview */}
              <div className="space-y-4">
                <Label>Texto da Mensagem</Label>
                <Textarea
                  value={generatedCaption}
                  onChange={(e) => setGeneratedCaption(e.target.value)}
                  rows={8}
                  className="resize-none"
                  placeholder="O texto será gerado aqui..."
                />
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MessageSquare className="w-4 h-4" />
                  {generatedCaption.length} caracteres
                </div>
              </div>
            </div>

            {/* Send Campaign Section */}
            <div className="mt-6 pt-6 border-t space-y-6">
              {/* Test Mode Indicator */}
              {testMode && (
                <Alert className="border-amber-200 bg-amber-50">
                  <TestTube className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>🧪 Modo Teste ativo:</strong> Apenas 1 mensagem será enviada para validar o fluxo.
                    Vá em <strong>Configurações</strong> para desativar o modo teste e enviar para todos.
                  </AlertDescription>
                </Alert>
              )}

              {!testMode && (
                <Alert className="border-green-200 bg-green-50">
                  <Radio className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <strong>🚀 Modo Live ativo:</strong> A campanha será enviada para <strong>{validAndActiveCount}</strong> consulentes válidos e ativos.
                  </AlertDescription>
                </Alert>
              )}

              {/* Info about invalid numbers */}
              {invalidConsulentes.length > 0 && (
                <Alert>
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    {invalidConsulentes.length} consulente(s) com telefone inválido não receberão a campanha. 
                    <Button variant="link" className="h-auto p-0 ml-1" onClick={() => {
                      setConsultentesExpanded(true);
                      setValidationTab('invalid');
                    }}>
                      Ver lista
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Interval Configuration */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Timer className="w-5 h-5 text-muted-foreground" />
                  <Label htmlFor="intervalo" className="whitespace-nowrap">Intervalo entre envios:</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="intervalo"
                    type="number"
                    min={1}
                    max={60}
                    value={intervalo}
                    onChange={(e) => setIntervalo(Math.max(1, Math.min(60, parseInt(e.target.value) || 5)))}
                    className="w-20"
                    disabled={sending}
                  />
                  <span className="text-sm text-muted-foreground">segundos</span>
                </div>
              </div>

              {/* Progress Section - only show when sending */}
              {sending && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Progresso do Envio {testMode && '(Teste)'}</span>
                    <span className="text-sm font-medium">{progressPercent}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-3" />
                  
                  {progress.consulenteAtual && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Send className="w-4 h-4 animate-pulse" />
                      Enviando para: <span className="font-medium">{progress.consulenteAtual}</span>
                      <span className="text-xs">({progress.atual}/{progress.total})</span>
                    </p>
                  )}
                  
                  <div className="flex items-center gap-6 text-sm">
                    <span className="flex items-center gap-1.5 text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      {progress.enviados} enviados
                    </span>
                    <span className="flex items-center gap-1.5 text-destructive">
                      <XCircle className="w-4 h-4" />
                      {progress.erros} erros
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      {progress.total - progress.atual} restantes
                    </span>
                  </div>
                </div>
              )}

              {/* Send / Cancel Buttons */}
              {sending ? (
                <Button
                  variant="destructive"
                  size="lg"
                  className="w-full"
                  onClick={handleCancelSend}
                >
                  <StopCircle className="w-4 h-4 mr-2" />
                  Cancelar Envio
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="lg"
                      className={`w-full ${testMode ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                      disabled={!generatedImageUrl || !generatedCaption || validAndActiveCount === 0}
                    >
                      {testMode ? (
                        <>
                          <TestTube className="w-4 h-4 mr-2" />
                          Enviar Teste (1 Consulente Válido)
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Enviar Campanha para {validAndActiveCount} Consulentes
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {testMode ? '🧪 Confirmar Envio de Teste' : '🚀 Confirmar Envio'}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {testMode ? (
                          <>
                            Você está em <strong>Modo Teste</strong>. Apenas <strong>1 mensagem</strong> será enviada 
                            para o primeiro consulente válido e ativo para validar o fluxo.
                          </>
                        ) : (
                          <>
                            Você está prestes a enviar esta campanha para <strong>{validAndActiveCount} consulentes válidos e ativos</strong> via WhatsApp.
                            O envio será feito com intervalo de {intervalo} segundos entre cada mensagem.
                            Esta ação não pode ser desfeita.
                            {invalidConsulentes.length > 0 && (
                              <span className="block mt-2 text-amber-600">
                                ⚠️ {invalidConsulentes.length} consulente(s) com telefone inválido serão ignorados.
                              </span>
                            )}
                          </>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSendCampaign}>
                        {testMode ? 'Sim, enviar teste' : 'Sim, enviar campanha'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
